import { ContainerDescription } from "@retailer/svelte/ListDetail/View/Utils/ProductContainerDetail";
import { pluralize } from "@shared/svelte/Modules/pluralize";
import Flash from "@shared/services/flashv2";
import { restrictRepAccess } from "@retailer/services/preview_forbidden_flash";
import { HttpPromise } from "@shared/svelte/Modules/ApiService";
import type { Order, RetailerOrder, SharedOrder } from "@shared/types/Order";
import type { SalesRep } from "@shared/types/SalesRep";
import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";
import type { RetailerType } from "@shared/types/Retailer";
import { get } from "svelte/store";

// This is needed to smooth out the different shapes in the two orders
export const getProductForDescription = product => {
  if (product.container_size) return product;
  return product.product;
};

export function copyOrderToClipboard(order: RetailerOrder | SharedOrder) {
  const salesReps = getSalesReps(order);

  let text = "";

  if (salesReps.length > 0) {
    const emails = salesReps
      .filter(rep => rep.contact_email?.length > 0)
      .map(rep => rep.contact_email)
      .join(", ");

    text += `To: ${emails}\n\n`;
  }

  if (!!order.po_number) {
    text += `PO # ${order.po_number}\nRep note - For tracking, please include PO # on the delivery invoice\n\n`;
  }

  text += "Order summary:";

  for (const product of order.products) {
    text += "\n  * " + product.product_line.name + "; ";
    text += ContainerDescription(getProductForDescription(product), !product.case_quantity) + "; ";

    if (product.case_quantity) {
      text += pluralize("case", product.case_quantity, true);
    }
    if (product.case_quantity && product.unit_quantity) {
      text += ", ";
    }
    if (product.unit_quantity) {
      text += pluralize(product.product.container_type, product.unit_quantity, true);
    }
    if (product.deal_notes) {
      text += "\n    * Deal note: " + product.deal_notes;
    }
    if (
      product.permanent_deal_notes &&
      product.permanent_deal_notes.notes &&
      product.permanent_deal_notes.notes !== product?.deal_notes
    ) {
      text += "\n    * Deal note: " + product.permanent_deal_notes.notes;
    }
  }
  if (order.retailer_notes) {
    text += "\n\nOrder note:\n" + order.retailer_notes;
  }
  if (order.backup_notes) {
    text += "\n\nBackup instructions:\n" + order.backup_notes;
  }
  navigator.clipboard.writeText(text).then(() => {
    Flash.now({
      type: "success",
      header: "Your order to " + order.distributor.name + " has been copied to clipboard",
      message: "Paste your order into an email or text and send it to your rep",
    });
  });
}

export const downloadPdf = (order: Order) => {
  if (restrictRepAccess()) return;
  window.open(`/api/retailer/orders/${order.id}/download`, "_blank");
};

export function formatSalesRepEmails(order: RetailerOrder | SharedOrder): string {
  const salesReps = getSalesReps(order);
  return salesReps
    .filter(rep => rep.contact_email?.length > 0)
    .map(rep => rep.contact_email)
    .join(",");
}

export function buildMailtoLink(
  order: RetailerOrder | SharedOrder,
  retailer: RetailerType,
  addresses: string,
): string {
  const accountNumber = order.account_number || order.distributor.account_number;
  const subject = `New Order for ${retailer.name}`;
  let body = `Hi, \n\nHere's my order for ${retailer.name}. Please respond to confirm receipt of this order.\n\nAddress:\n${retailer.address1}\n${retailer.city}, ${retailer.state} ${retailer.zip}`;
  if (accountNumber) {
    body += ` (Account #${accountNumber})`;
  }
  body += "\n";

  if (!!order.po_number) {
    body += `\nPO # ${order.po_number}\nRep note - For tracking, please include PO # on the delivery invoice\n`;
  }

  body += "\nOrder:";
  for (const product of order.products) {
    const type = product.container_type || product.product.container_type;

    body += `\n- ${product.product_line.name}; `;
    body += `${ContainerDescription(getProductForDescription(product), !product.case_quantity)}; `;

    if (product.case_quantity) {
      body += `(${pluralize("case", product.case_quantity, true)})`;
    }
    if (product.case_quantity && product.unit_quantity) {
      body += ", ";
    }
    if (product.unit_quantity) {
      body += `${pluralize(type, product.unit_quantity, true)}`;
    }
    if (product.deal_notes) {
      body += ` (${product.deal_notes})`;
    }
    if (
      product.permanent_deal_notes &&
      product.permanent_deal_notes.notes &&
      product.permanent_deal_notes.notes !== product?.deal_notes
    ) {
      body += ` (${product.permanent_deal_notes.notes})`;
    }
  }

  if (order.retailer_notes) {
    body += `\n\n${order.retailer_notes}`;
  }
  if (order.backup_notes) {
    body += `\n\n${order.backup_notes}`;
  }

  let user = get(CurrentUserStore);

  body += `\n\nThanks,\n${user.first_name} ${user.last_name}`;

  const params = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return `mailto:${addresses}?${params}`;
}

export const updateManualActionRequired = async (order: Order, onCartSuccess = false) => {
  try {
    const response = await HttpPromise({
      url: `api/retailer/orders/${order.id}/update_manual_action_required`,
      method: "PUT",
      data: { order, on_cart_success: onCartSuccess },
    });
    return response;
  } catch {
    Flash.now({
      type: "error",
      header: "Your order could not be marked completed",
      message: "Please contact support if this error persists",
    });
  }
};

function getSalesReps(order: RetailerOrder | SharedOrder): SalesRep[] {
  if (order.sales_reps?.length > 0) {
    return order.sales_reps;
  } else if (order.cart_sales_reps?.length > 0) {
    return order.cart_sales_reps;
  } else {
    return [];
  }
}
